import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { ExportInvoiceService } from '../../services/exportInvoice.service';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { PERMISSIONS } from '../../constants/permissions';
import { IExportInvoice } from '../../interfaces/exportInvoice.interface';
import RefAutocomplete, { RefOption } from './common/RefAutocomplete';
import { fetchAllPages } from './common/fetchAllPages';
import { formatMoney } from '../../utils/format';
import { enumLabel } from '../../utils/garmentsLabels';
import { formatDate } from '../../utils/dateUtils';
import { sortByDateDescFutureLast } from '../../utils/listDefaults';

/** Export invoice register (15.1) -- mirrors PackingListList's list/paginate shape, plus a filter
 * toolbar (buyer order, search, date-from/to). Reuses EXP_VIEW/EXP_MANAGE (spec §3 -- no new
 * permission catalog entry for this lane). Toolbar controls are flex cells per house convention
 * (CLAUDE.md "Frontend UI conventions") -- never a fixed px width, never a bare fullWidth in a row. */
const ExportInvoiceList: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.EXP_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;

    const [rows, setRows] = useState<IExportInvoice[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [loading, setLoading] = useState(true);

    const [orderLabelById, setOrderLabelById] = useState<Record<string, string>>({});
    const [buyerOrderOptions, setBuyerOrderOptions] = useState<RefOption[]>([]);
    const [buyerOrderId, setBuyerOrderId] = useState<string | null>(null);
    const [q, setQ] = useState('');
    const [debouncedQ, setDebouncedQ] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedQ(q), 300);
        return () => clearTimeout(handle);
    }, [q]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const buyerOrders = await fetchAllPages((p, s) => BuyerOrderService.list(institutionId, p, s));
                if (cancelled) return;
                setOrderLabelById(Object.fromEntries(buyerOrders.map(o => [o.id, o.orderNumber])));
                setBuyerOrderOptions(buyerOrders.map(o => ({
                    id: o.id, label: o.orderDate ? `${o.orderNumber} — ${formatDate(o.orderDate)}` : o.orderNumber,
                })));
            } catch (e) {
                if (!cancelled) toast.showApiError(e, 'Failed to load buyer order options');
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const load = async () => {
        try {
            setLoading(true);
            const res = await ExportInvoiceService.list(institutionId, {
                buyerOrderId: buyerOrderId ?? undefined,
                q: debouncedQ.trim() || undefined,
                from: from || undefined,
                to: to || undefined,
                page, size,
            });
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load export invoices');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [page, size, buyerOrderId, debouncedQ, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.invList.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Export Invoices</Typography>
                {canManage && (
                    <Button data-help="commercial.invList.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/commercial/export-invoices/new')}>
                        New Export Invoice
                    </Button>
                )}
            </Box>

            <Box data-help-section="commercial.invList.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <RefAutocomplete
                    data-help="commercial.invList.filter.order"
                    label="Buyer Order"
                    options={buyerOrderOptions}
                    value={buyerOrderId}
                    onChange={(newId) => { setBuyerOrderId(newId); setPage(0); }}
                    sx={{ flex: '1 1 240px', maxWidth: 300 }}
                />
                <TextField
                    data-help="commercial.invList.filter.search"
                    label="Search (invoice #)" size="small" value={q}
                    onChange={(e) => { setQ(e.target.value); setPage(0); }}
                    sx={{ flex: '1 1 240px', maxWidth: 300 }}
                />
                <TextField
                    data-help="commercial.invList.filter.from"
                    label="From" type="date" size="small" InputLabelProps={{ shrink: true }} value={from}
                    onChange={(e) => { setFrom(e.target.value); setPage(0); }}
                    sx={{ flex: '1 1 240px', maxWidth: 300 }}
                />
                <TextField
                    data-help="commercial.invList.filter.to"
                    label="To" type="date" size="small" InputLabelProps={{ shrink: true }} value={to}
                    onChange={(e) => { setTo(e.target.value); setPage(0); }}
                    sx={{ flex: '1 1 240px', maxWidth: 300 }}
                />
            </Box>

            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="commercial.invList.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.invList.col.number">Invoice #</TableCell>
                                <TableCell data-help="commercial.invList.col.date">Date</TableCell>
                                <TableCell data-help="commercial.invList.col.order">Buyer Order</TableCell>
                                <TableCell data-help="commercial.invList.col.consignee">Consignee</TableCell>
                                <TableCell data-help="commercial.invList.col.lc">LC #</TableCell>
                                <TableCell data-help="commercial.invList.col.incoterm">Incoterm</TableCell>
                                <TableCell data-help="commercial.invList.col.currency">Currency</TableCell>
                                <TableCell align="right" data-help="commercial.invList.col.qty">Total Quantity</TableCell>
                                <TableCell align="right" data-help="commercial.invList.col.amount">Total Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortByDateDescFutureLast(rows, x => x.invoiceDate).map(row => (
                                <TableRow key={row.id} hover sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/commercial/export-invoices/${row.id}`)}>
                                    <TableCell>{row.invoiceNumber}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.invoiceDate)}</TableCell>
                                    <TableCell>{orderLabelById[row.buyerOrderId] ?? '—'}</TableCell>
                                    <TableCell>{row.consigneeName ?? '—'}</TableCell>
                                    <TableCell>{row.lcNumber ?? '—'}</TableCell>
                                    <TableCell>{enumLabel(row.incoterm)}</TableCell>
                                    <TableCell>{row.currencyCode ?? '—'}</TableCell>
                                    <TableCell align="right">{row.totalQuantity}</TableCell>
                                    <TableCell align="right">{formatMoney(row.totalAmount, row.currencyCode)}</TableCell>
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={9} align="center">No export invoices found.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={size}
                        onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                    />
                </TableContainer>
            )}
        </Box>
    );
};

export default ExportInvoiceList;
